import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Moon, KeyRound, Coffee, TrainFront, Wifi, Copy, Lock, MapPin,
  Navigation, Bus, Footprints, Sparkles, ArrowRight, ArrowDown, Check,
} from "lucide-react";
import { LogoSymbol } from "@/components/Brand";
import { ConciergeIcon } from "@/components/ConciergeIcon";
import { CTA_PRIMARY } from "@/lib/plans";
import { sb } from "@/lib/text";

const NAVY = "#0f2444";

function Bubble({ children }) {
  return (
    <div className="flex justify-end">
      <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-blue-600 text-white text-[13px] leading-snug px-3.5 py-2.5 shadow-sm">{children}</div>
    </div>
  );
}

function AnswerCard({ children }) {
  return <div className="rounded-2xl bg-white border border-blue-100 shadow-sm shadow-blue-900/5 p-3.5">{children}</div>;
}

function WifiScene() {
  return (
    <AnswerCard>
      <div className="flex items-center gap-2 text-blue-600"><span className="w-8 h-8 rounded-xl bg-blue-50 grid place-items-center"><Wifi className="w-4 h-4" /></span><span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Wi-Fi</span></div>
      <div className="mt-3 space-y-2">
        <div className="flex items-center justify-between"><span className="text-[12px] text-slate-400">Rete</span><span className="text-[13px] font-semibold text-slate-800">CasaAurora_Guest</span></div>
        <div className="flex items-center justify-between"><span className="text-[12px] text-slate-400">Password</span><span className="text-[13px] font-mono font-semibold text-slate-800">aurora2026</span></div>
      </div>
      <button className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-xl bg-blue-600 text-white text-[12px] font-semibold py-2.5"><Copy className="w-3.5 h-3.5" /> Copia password</button>
    </AnswerCard>
  );
}

function AccessScene() {
  const steps = [["Portone: citofono interno 12", false], ["Ascensore fino al 3° piano", false], ["Keybox a destra della porta", true]];
  return (
    <AnswerCard>
      <div className="flex items-center gap-2 text-blue-600"><span className="w-8 h-8 rounded-xl bg-blue-50 grid place-items-center"><KeyRound className="w-4 h-4" /></span><span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Accesso alla struttura</span></div>
      <div className="mt-3 space-y-2">
        {steps.map(([s, locked], i) => (
          <div key={i} className="flex items-center gap-2.5">
            <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-[11px] font-bold grid place-items-center shrink-0">{i + 1}</span>
            <span className="text-[12.5px] text-slate-700 flex-1">{s}</span>
            {locked && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded-full"><Lock className="w-2.5 h-2.5" /> Protetto</span>}
          </div>
        ))}
      </div>
    </AnswerCard>
  );
}

function TipsScene() {
  return (
    <AnswerCard>
      <div className="flex items-center gap-2"><span className="w-9 h-9 rounded-full bg-white border border-blue-100 grid place-items-center shrink-0"><ConciergeIcon className="w-6 h-6" /></span><div><p className="text-[12px] font-bold text-slate-800 leading-tight">Concierge AI</p><p className="text-[10px] text-emerald-500 font-semibold">● online</p></div></div>
      <p className="mt-3 text-[13px] leading-snug text-slate-700">Per la colazione ti consiglio <strong>Bar Aurora</strong>, a 4 minuti a piedi: cornetti ottimi e caffè locale ☕</p>
      <button className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-blue-50 text-blue-700 text-[12px] font-semibold px-3 py-2"><MapPin className="w-3.5 h-3.5" /> Apri sulla mappa</button>
    </AnswerCard>
  );
}

function MoveScene() {
  const modes = [["Mezzi", Bus], ["A piedi", Footprints], ["Maps", Navigation]];
  return (
    <AnswerCard>
      <div className="flex items-center gap-2 text-blue-600"><span className="w-8 h-8 rounded-xl bg-blue-50 grid place-items-center"><TrainFront className="w-4 h-4" /></span><span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Come muoversi</span></div>
      <div className="mt-3 rounded-xl bg-blue-50/60 border border-blue-100 p-2.5 flex items-center gap-2"><MapPin className="w-4 h-4 text-blue-600" /><span className="text-[12.5px] text-slate-700">Metro M3 · fermata Duomo · 12 min</span></div>
      <div className="mt-2.5 grid grid-cols-3 gap-2">
        {modes.map(([l, I]) => (
          <div key={l} className="flex flex-col items-center gap-1 rounded-xl border border-blue-100 py-2.5"><span className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 grid place-items-center"><I className="w-4 h-4" /></span><span className="text-[10.5px] font-semibold text-slate-600">{l}</span></div>
        ))}
      </div>
    </AnswerCard>
  );
}

const PHOTO = {
  wifi: "https://images.unsplash.com/photo-1722509522928-12f21734ce88?crop=entropy&cs=srgb&fm=jpg&q=80&w=640&h=800&fit=crop&ixlib=rb-4.1.0",
  access: "https://images.unsplash.com/photo-1573854612183-873dd713cc8b?crop=entropy&cs=srgb&fm=jpg&q=80&w=640&h=800&fit=crop&ixlib=rb-4.1.0",
  tips: "https://images.unsplash.com/photo-1551727609-1f89c019b5ca?crop=entropy&cs=srgb&fm=jpg&q=80&w=640&h=800&fit=crop&ixlib=rb-4.1.0",
  move: "https://images.unsplash.com/photo-1707037699637-e2fd8f612d46?crop=entropy&cs=srgb&fm=jpg&q=80&w=640&h=800&fit=crop&ixlib=rb-4.1.0",
};

const TABS = [
  { id: "wifi", time: "23:47", icon: Moon, label: "Wi-Fi", photo: PHOTO.wifi, q: "Ciao, qual è la password del Wi-Fi?", headline: "Tu dormi. StayGuide continua a rispondere.", copy: "Niente più messaggi per informazioni che hai già comunicato decine di volte.", Scene: WifiScene },
  { id: "access", time: "00:32", icon: KeyRound, label: "Accesso", photo: PHOTO.access, q: "Non riesco a capire come aprire il portone.", headline: "Le istruzioni giuste, proprio quando servono.", copy: "L'ospite trova autonomamente indicazioni chiare per entrare nella struttura, anche nel cuore della notte.", Scene: AccessScene },
  { id: "tips", time: "08:15", icon: Coffee, label: "Consigli", photo: PHOTO.tips, q: "Dove posso fare una buona colazione qui vicino?", headline: "Il tuo ospite chiede. StayGuide risponde.", copy: "Consigli, informazioni sulla struttura e risposte alle domande più frequenti sempre a portata di mano.", Scene: TipsScene },
  { id: "move", time: "17:40", icon: TrainFront, label: "Come muoversi", photo: PHOTO.move, q: "Come arrivo in centro con i mezzi?", headline: "Meno indicazioni da spiegare. Più autonomia per l'ospite.", copy: "Percorsi, trasporti e informazioni utili sono disponibili direttamente dalla guida.", Scene: MoveScene },
];

const CHIPS = ["Wi-Fi", "Check-in", "Accesso", "Regole", "Trasporti", "Consigli", "FAQ"];

export function TimeSaved() {
  const [active, setActive] = useState("wifi");
  const tab = TABS.find((t) => t.id === active);
  const Scene = tab.Scene;

  return (
    <section className="bg-white py-16 sm:py-20 overflow-hidden" data-testid="time-saved-section">
      <div className="max-w-6xl mx-auto px-5">
        {/* Header */}
        <div className="max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-widest text-blue-600">Il tuo tempo conta</p>
          <h2 className="mt-2 font-display font-bold text-3xl sm:text-4xl tracking-tight" style={{ color: NAVY }}>
            Meno messaggi. Meno interruzioni. Più tempo per te.
          </h2>
          <p className="mt-3 text-slate-500 text-base">
            {sb("Le domande degli ospiti arrivano a qualsiasi ora. Con StayGuide, molte risposte sono già pronte prima ancora che debbano scriverti.")}
          </p>
        </div>

        {/* Situation photo cards — mobile carousel (~1.4 per view) / desktop 4-up */}
        <div className="mt-8 flex gap-3 sm:gap-4 overflow-x-auto sm:overflow-visible hide-scrollbar snap-x snap-mandatory -mx-5 px-5 sm:mx-0 sm:px-0">
          {TABS.map((t) => {
            const on = t.id === active;
            return (
              <button key={t.id} onClick={() => setActive(t.id)} data-testid={`time-tab-${t.id}`}
                aria-pressed={on}
                className={`relative snap-start shrink-0 w-[66%] xs:w-[58%] sm:w-auto sm:flex-1 aspect-[4/5] rounded-3xl overflow-hidden text-left group transition-all duration-300 ${on ? "ring-2 ring-blue-500 ring-offset-2 ring-offset-white shadow-xl shadow-blue-900/20" : "ring-1 ring-black/5 hover:-translate-y-0.5 shadow-md shadow-blue-900/5"}`}>
                <img src={t.photo} alt={t.label} loading="lazy"
                  className={`absolute inset-0 w-full h-full object-cover transition-transform duration-700 ${on ? "scale-105" : "group-hover:scale-105"}`} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-transparent" />
                <span className="absolute top-3 left-3 w-8 h-8 rounded-full bg-white/15 backdrop-blur-md ring-1 ring-white/25 grid place-items-center text-white">
                  <t.icon className="w-4 h-4" />
                </span>
                {on && <span className="absolute top-3.5 right-3.5 w-2.5 h-2.5 rounded-full bg-blue-400 ring-4 ring-blue-400/30" />}
                <div className="absolute bottom-3 left-3.5 right-3.5">
                  <div className="font-mono font-bold text-white text-3xl tabular-nums leading-none drop-shadow-md">{t.time}</div>
                  <div className="mt-1.5 text-white text-sm font-semibold drop-shadow">{t.label}</div>
                </div>
              </button>
            );
          })}
        </div>
        <p className="mt-3 text-center text-[12px] text-slate-400 sm:hidden">Scorri e tocca una situazione →</p>

        {/* Dynamic container */}
        <div className="mt-5 rounded-3xl border border-blue-100 bg-gradient-to-b from-blue-50/60 to-white p-5 sm:p-8">
          <AnimatePresence mode="wait">
            <motion.div key={active}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="grid lg:grid-cols-2 gap-8 items-center">
              {/* Scene */}
              <div className="max-w-sm w-full mx-auto lg:mx-0 space-y-2.5">
                <div className="inline-flex items-center gap-1.5 rounded-full bg-white border border-blue-100 px-2.5 py-1 text-[11px] font-bold text-slate-500 shadow-sm">
                  <tab.icon className="w-3 h-3 text-blue-600" /> {tab.time}
                </div>
                <Bubble>{tab.q}</Bubble>
                <div className="flex justify-center py-0.5"><ArrowDown className="w-4 h-4 text-blue-300" /></div>
                <Scene />
              </div>
              {/* Text */}
              <div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-100 text-blue-700 text-[11px] font-bold px-2.5 py-1"><Check className="w-3 h-3" /> Già nella guida</span>
                <h3 className="mt-3 font-display font-bold text-2xl sm:text-3xl tracking-tight leading-tight" style={{ color: NAVY }}>{tab.headline}</h3>
                <p className="mt-3 text-slate-500 text-base leading-relaxed">{sb(tab.copy)}</p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Final block */}
        <div className="mt-8 rounded-3xl bg-blue-50 border border-blue-100 p-7 sm:p-10 text-center">
          <h3 className="font-display font-bold text-2xl sm:text-3xl tracking-tight" style={{ color: NAVY }}>
            Le stesse domande. Ogni soggiorno. Ogni giorno.
          </h3>
          <p className="mt-3 text-slate-500 max-w-xl mx-auto">
            StayGuide raccoglie le informazioni che ripeti continuamente e le rende disponibili ai tuoi ospiti 24 ore su 24.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            {CHIPS.map((c) => (
              <span key={c} className="rounded-full bg-white border border-blue-100 text-slate-700 text-[13px] font-semibold px-3.5 py-1.5">{c}</span>
            ))}
          </div>
          <div className="flex justify-center py-3"><ArrowDown className="w-5 h-5 text-blue-400" /></div>
          <div className="flex justify-center"><LogoSymbol className="h-9" /></div>
          <div className="flex justify-center py-3"><ArrowDown className="w-5 h-5 text-blue-400" /></div>
          <p className="font-display font-bold text-lg sm:text-xl" style={{ color: NAVY }}>
            Meno messaggi per te. Più autonomia per i tuoi ospiti.
          </p>
          <Link to="/signup" data-testid="time-saved-cta"
            className="mt-6 inline-flex items-center justify-center gap-2 bg-blue-600 text-white font-semibold rounded-full px-7 py-3.5 shadow-lg shadow-blue-600/25 hover:bg-blue-700 transition-colors">
            {CTA_PRIMARY} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
